/**
 * Billing area layout — owns the AppNav chrome and the active system-notice
 * banner for every /billing/* route, deriving LeftNav highlighting from the
 * matched path so individual billing pages carry neither their own `page`
 * id nor their own banner fetch. Each child route keeps its own RequireAuth
 * guard (accounting-user vs. staff), since guard rules differ per page.
 *
 * Contains:
 * - BillingLayout — route layout component (default export)
 */
import { Box, Skeleton, Stack } from '@mui/material';
import { isAfter, isBefore, parseISO } from 'date-fns';
import React, { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import SystemNoticeBanner from '@sophocles/components/SystemNotice/SystemNoticeBanner';
import { fetchActiveSystemBanner } from '@sophocles/entities/SystemBanner';

/**
 * Resolves a `/billing/*` pathname to the AppNav `page` id LeftNav
 * highlights on. Longest-prefix wins: any depth under
 * `/billing/accounts/:accountId` (general, pricing, invoices, trial,
 * catalog) resolves to the account page id before the bare accounts list
 * falls through to the list page id.
 *
 * @param {string} pathname - location.pathname of the active route.
 * @returns {string} the AppNav `page` id to render.
 */
function resolveBillingPage(pathname) {
  const subpath = pathname.replace(/^\/billing\/?/, '').replace(/\/$/, '');
  const segments = subpath.split('/').filter(Boolean);

  if (segments[0] === 'accounts' && segments.length > 1) return 'billing_account';
  if (segments[0] === 'accounts') return 'billing_accounts';
  if (subpath === 'prepaid-tracking') return 'billing_admin_prepaid-agreements';
  if (subpath === 'trial-units') return 'billing_reports';
  // Invoice detail has no LeftNav entry of its own; keep its historical page
  // id so AppNav's chrome (document title, mobile header) stays consistent.
  if (segments[0] === 'invoices') return 'billing_invoicing_detail';
  return 'billing_accounts';
}

// Suspense fallback for lazy billing pages: skeleton the content region so a
// chunk load never blanks the whole screen — the app-level RouteLoader
// fallback would otherwise unmount the nav/title chrome on every first visit
// to a billing page.
function BillingPageSkeleton() {
  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, px: 'var(--page-px)', py: 'var(--page-py)' }}>
      <Skeleton variant="rounded" height={40} sx={{ maxWidth: 420 }} />
      <Skeleton variant="rounded" height={96} />
      <Skeleton variant="rounded" sx={{ flex: 1 }} />
    </Stack>
  );
}

function BillingLayout(props) {
  const location = useLocation();
  const resolvedPage = resolveBillingPage(location.pathname);
  const { user } = useUser();
  const isStaff = user.get('is_staff', false);

  // Fetched once for the whole billing session (this layout persists across
  // /billing/* navigation) rather than per-page, unlike RequireAuth's
  // per-mount fetch — same source, same rule, fewer redundant requests.
  const [banner, setBanner] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchActiveSystemBanner()
      .then(result => {
        if (!cancelled) setBanner(result && !result.error ? result : null);
      })
      .catch(() => {
        if (!cancelled) setBanner(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showNotice =
    banner &&
    banner.enabled &&
    (!banner.staff_only || isStaff) &&
    isBefore(new Date(), parseISO(banner.end_time)) &&
    isAfter(new Date(), parseISO(banner.start_time));
  const notice = showNotice ? <SystemNoticeBanner close={() => setBanner(null)} html={banner.html} /> : null;

  return (
    <AppNav flex notFullHeight loading={false} page={resolvedPage} notice={notice} {...props}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Suspense fallback={<BillingPageSkeleton />}>
          <Outlet />
        </Suspense>
      </Box>
    </AppNav>
  );
}

export default BillingLayout;
